import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaHistory, 
  FaCalendarAlt, 
  FaCheck, 
  FaTimes, 
  FaSpinner,
  FaClock,
  FaInbox,
  FaClipboardList,
  FaCommentAlt,
  FaBan
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import './LeaveHistory.css';
import Layout from '../layouts/Layout';

const LeaveHistory = () => {
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const baseUrl = "https://api.burrayhr.com";

  useEffect(() => {
    fetchLeaveHistory();
  }, []);

  const fetchLeaveHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/leaves/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLeaveHistory(response.data);
    } catch (error) {
      console.error('Error fetching leave history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays === 1 ? '1 day' : `${diffDays} days`;
  };

  const handleCancelLeave = async (leave, e) => {
    if (e) e.stopPropagation(); // Prevent card click when clicking cancel button
    
    try {
      const result = await Swal.fire({
        title: 'Cancel Leave Request',
        text: 'Are you sure you want to cancel this leave request?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#64748b',
        confirmButtonText: 'Yes, cancel it',
        cancelButtonText: 'No, keep it'
      });

      if (result.isConfirmed) {
        const token = localStorage.getItem('token');
        await axios.patch(`${baseUrl}/api/leaves/${leave.id}/`, {
          status: 'cancelled'
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Update the leave status in the local state
        setLeaveHistory(prevHistory => 
          prevHistory.map(item => 
            item.id === leave.id 
              ? { ...item, status: 'cancelled' }
              : item
          )
        );

        // Update selected leave if it's currently being viewed
        if (selectedLeave && selectedLeave.id === leave.id) {
          setSelectedLeave(prev => ({ ...prev, status: 'cancelled' }));
        }

        Swal.fire({
          icon: 'success',
          title: 'Leave Request Cancelled',
          text: 'Your leave request has been cancelled successfully.',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error cancelling leave:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to cancel leave request'
      });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const handleCardClick = (leave) => {
    setSelectedLeave(leave);
    setShowModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="leave-history">
          <div className="leave-header">
            <FaSpinner className="header-icon fa-spin" />
            <h2>Loading leave history...</h2>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="leave-history">
        <div className="leave-header">
          <FaHistory style={{color: 'navy'}} className="" />
          <h2>Leave History</h2>
        </div>

        {leaveHistory.length === 0 ? (
          <div className="empty-state">
            <FaInbox className="empty-state-icon" />
            <h3 className="empty-state-text">No Leave History</h3>
            <p className="empty-state-subtext">You haven't made any leave requests yet.</p>
          </div>
        ) : (
          <div className="leave-cards-container">
            {leaveHistory.map(leave => (
              <div 
                key={leave.id} 
                className="leave-card"
                onClick={() => handleCardClick(leave)}
              >
                <div className="card-header">
                  <h3 className="leave-type">
                    <FaClipboardList />
                    {leave.leave_type} Leave
                  </h3>
                  {leave.status === 'pending' && (
                    <button 
                      style={{backgroundColor: '#edd491', color: 'white !important', borderRadius: '0.5rem', border: 'none', padding: '0.5rem 1rem'}}
                      className="cancel-leave-btn"
                      onClick={(e) => handleCancelLeave(leave, e)}
                      title="Cancel Leave Request"
                    >
                      <FaBan /> Cancel
                    </button>
                  )}
                </div>

                <div className="card-body">
                  <div className="leave-info">
                    <div className="info-item">
                      <span className="info-label">Start Date</span>
                      <span className="info-value">
                        <FaCalendarAlt /> {formatDate(leave.start_date)}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">End Date</span>
                      <span className="info-value">
                        <FaCalendarAlt /> {formatDate(leave.end_date)}
                      </span>
                    </div>
                  </div>

                  <div className="duration-badge">
                    <FaClock /> {leave.duration} {leave.leave_type.toLowerCase() === 'maternity' || leave.leave_type.toLowerCase() === 'sick' ? 'days' : 'working days'}
                  </div>

                  <span className={`status-badge ${getStatusBadgeClass(leave.status)}`}>
                    {leave.status === 'approved' ? <FaCheck /> : 
                     leave.status === 'rejected' ? <FaTimes /> : 
                     leave.status === 'cancelled' ? <FaBan /> :
                     <FaSpinner className="fa-spin" />}
                    {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                  </span>

                  {leave.reason && (
                    <div className="reason-text">
                      <strong>Reason for Leave:</strong>
                      <p>{leave.reason}</p>
                    </div>
                  )}

                  {(leave.status === 'approved' || leave.status === 'rejected') && leave.response_message && (
                    <div className={`response-message ${leave.status === 'approved' ? 'approved-message' : 'rejected-message'}`}>
                      <strong>{leave.status === 'approved' ? 'Manager\'s Response:' : 'Rejection Reason:'}</strong>
                      <p>{leave.response_message}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && selectedLeave && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Leave Details</h3>
                {selectedLeave.status === 'pending' && (
                  <button 
                    className="cancel-leave-btn modal-cancel-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelLeave(selectedLeave, e);
                      setShowModal(false);
                    }}
                  >
                    <FaBan /> Cancel Leave
                  </button>
                )}
                <button className="close-button" onClick={() => setShowModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-content">
                <div className="leave-info">
                  <div className="info-item">
                    <span className="info-label">Leave Type</span>
                    <span className="info-value">
                      <FaClipboardList /> {selectedLeave.leave_type} Leave
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Duration</span>
                    <span className="info-value">
                      <FaClock /> {calculateDuration(selectedLeave.start_date, selectedLeave.end_date)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Start Date</span>
                    <span className="info-value">
                      <FaCalendarAlt /> {formatDate(selectedLeave.start_date)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">End Date</span>
                    <span className="info-value">
                      <FaCalendarAlt /> {formatDate(selectedLeave.end_date)}
                    </span>
                  </div>
                </div>

                <span className={`status-badge ${getStatusBadgeClass(selectedLeave.status)}`}>
                  {selectedLeave.status === 'approved' ? <FaCheck /> : 
                   selectedLeave.status === 'rejected' ? <FaTimes /> : 
                   selectedLeave.status === 'cancelled' ? <FaBan /> :
                   <FaSpinner className="fa-spin" />}
                  {selectedLeave.status.charAt(0).toUpperCase() + selectedLeave.status.slice(1)}
                </span>

                {selectedLeave.reason && (
                  <div className="reason-text">
                    <strong>Reason for Leave:</strong>
                    <p>{selectedLeave.reason}</p>
                  </div>
                )}

                {(selectedLeave.status === 'approved' || selectedLeave.status === 'rejected') && selectedLeave.response_message && (
                  <div className={`response-message ${selectedLeave.status === 'approved' ? 'approved-message' : 'rejected-message'}`}>
                    <strong>{selectedLeave.status === 'approved' ? 'Manager\'s Response:' : 'Rejection Reason:'}</strong>
                    <p>{selectedLeave.response_message}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const styles = `
  .cancel-leave-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #fee2e2;
    color: #ef4444;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .cancel-leave-btn:hover {
    background-color: #fecaca;
  }

  .modal-cancel-btn {
    margin-right: 1rem;
  }

  .status-cancelled {
    background-color: #fee2e2;
    color: #ef4444;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
`;

export default LeaveHistory;